"use client";

import type * as React from "react";
import { cn } from "@/lib/utils";

export type McpReviewCountChipProps = {
  /** Total selected tools — shown as the primary count. */
  toolCount: number;
  /** Total selected MCP servers — shown as the secondary count after a divider. */
  serverCount: number;
  /**
   * The chip is currently in its pressed/open state — used when a click on
   * the chip toggles a review pane. Adds an external ring so the active
   * state reads at a glance without being confused with hover.
   */
  active?: boolean;
} & Omit<React.ComponentProps<"button">, "children" | "type">;

/**
 * Compact button-chip summarising the current selection — "N tools · M
 * servers". Sits in the picker's selection summary bar and toggles the grouped
 * review pane when clicked. Resting tint hints "clickable"; the active state
 * adds an external ring (same family as the server pills' focus ring) so the
 * user can see where the click landed.
 */
export function McpReviewCountChip({
  toolCount,
  serverCount,
  active = false,
  className,
  "aria-label": ariaLabel,
  ...rest
}: McpReviewCountChipProps) {
  const toolWord = toolCount === 1 ? "tool" : "tools";
  const serverWord = serverCount === 1 ? "server" : "servers";
  // Default the accessible name from the visible counts so screen-reader users
  // hear them too — a static label here would override the rendered text.
  const accessibleLabel =
    ariaLabel ??
    `View selection: ${toolCount} ${toolWord}, ${serverCount} ${serverWord}`;

  return (
    <button
      {...rest}
      aria-expanded={active}
      aria-label={accessibleLabel}
      className={cn(
        "flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-foreground/[0.04] px-2 py-1 font-medium text-xs transition-[background-color,box-shadow,color] duration-[var(--duration-fast)] ease-[var(--ease-smooth)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 motion-reduce:transition-colors",
        active
          ? "text-foreground ring-2 ring-ring/60"
          : "hover:bg-foreground/10",
        className
      )}
      data-slot="mcp-review-count-chip"
      data-status={active ? "open" : "closed"}
      type="button"
    >
      <span className="tabular-nums">{toolCount}</span>
      <span className="text-muted-foreground">{toolWord}</span>
      <span aria-hidden className="text-muted-foreground/60">
        ·
      </span>
      <span className="tabular-nums">{serverCount}</span>
      <span className="text-muted-foreground">{serverWord}</span>
    </button>
  );
}
